import { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
// @ts-ignore
import Knob from './Knob';
import S from './KnobChartWidgetStyles';
import { ThemeType } from '../../../theme/themeType';

const ANIMATION_DURATION = 600;

type Props = {
  value: number | string;
  max: number;
  title?: string;
  subtitle?: string;
};

type CustomInputProps = {
  value: number | string;
  subtitle?: string;
};

function CustomInput({ value, subtitle }: CustomInputProps): JSX.Element {
  return (
    <div className="custom-input">
      <p className="title">{value}</p>
      {!!subtitle && <p className="subtitle">{subtitle}</p>}
    </div>
  );
}

function useAnimatedValue(target: number | string): { knobValue: number; labelValue: number | string; isNA: boolean } {
  const numericTarget = typeof target === 'number' ? target : 0;
  const isNA = typeof target !== 'number';
  const [displayed, setDisplayed] = useState(0);
  const [showNA, setShowNA] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const fromRef = useRef(0);

  useEffect(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    startRef.current = null;
    setShowNA(false);

    const from = fromRef.current;
    const to = numericTarget;

    const animate = (timestamp: number): void => {
      if (startRef.current === null) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const t = Math.min(elapsed / ANIMATION_DURATION, 1);
      const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      const current = from + (to - from) * eased;
      const rounded = Math.round(current);
      setDisplayed(rounded);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        fromRef.current = to;
        if (isNA) setShowNA(true);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return (): void => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current); };
  }, [target]);

  return { knobValue: displayed, labelValue: showNA ? target : displayed, isNA: showNA };
}

function KnobChartWidget({
  value,
  max,
  title,
  subtitle,
}: Props): JSX.Element {
  const { knobValue, labelValue, isNA } = useAnimatedValue(value);
  const theme = useTheme() as ThemeType;
  const bgColor = theme.colors.outline;
  const fgColor = theme.colors.main_cta;
  const inputColor = theme.colors.text;
  const handleChange = (newValue: number): void => {
    console.info(newValue);
  };

  return (
    <S.Wrapper>
      {!!title && <div className="title">{title}</div>}
      <Knob
        value={knobValue}
        onChange={handleChange}
        angleOffset={90}
        max={max}
        lineCap="round"
        displayInput={false}
        displayCustom={() => CustomInput({ value: labelValue, subtitle })}
        inputColor={inputColor}
        fgColor={isNA ? bgColor : fgColor}
        bgColor={bgColor}
        thickness={0.1}
        width={180}
        height={180}
        readOnly
      />
    </S.Wrapper>
  );
}

export default KnobChartWidget;
